import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * Default landing per role — used both when a signed-in user hits a
 * public route (e.g. `/`) and when they land on a route their role
 * isn't allowed to see.
 *
 * SALES-ROLE-001A · The Sales role lands on the existing Operations
 * dashboard (`/operations`) which the route configuration explicitly
 * admits Sales through (see App.js). Reusing the Operations dashboard
 * shell + visual language avoids creating a parallel design in this
 * pass. Unknown roles fall back to the public landing (`/`) so a
 * mis-typed or newly-introduced role can never re-enter a role guard
 * that redirects back to the same unknown role — the original
 * pre-fix blank-screen loop.
 */
export function homeForRole(role) {
    if (role === "operations") return "/operations";
    if (role === "installer")  return "/installer";
    if (role === "customer")   return "/dashboard";
    if (role === "sales")      return "/sales";
    return "/";
}

// DEV-013A · When `force_password_change=true` (typically after an
// administrator has issued a temporary password), the app must NOT
// serve normal application routes until the user has set a new
// password. Any authorised route other than the change-password page
// itself, the logout endpoint hooks and public legal pages must
// bounce the user to `/settings/change-password`.
const FORCED_CHANGE_ALLOWED = new Set([
    "/settings/change-password",
    "/legal/privacy",
    "/legal/terms",
    "/legal/licences",
    "/forgot-password",
    "/reset-password",
]);

/**
 * Route guard.
 *
 *   <ProtectedRoute>                                       // any signed-in user
 *   <ProtectedRoute role="installer">                       // single role
 *   <ProtectedRoute roles={["installer","operations"]}>     // multi-role
 *   <ProtectedRoute permission="manage_users">              // permission check
 *
 * `permission` is OR-ed against the user's `permissions` array. It's
 * checked in addition to `role`/`roles` so a route can require both
 * "operations role" AND "manage_users permission" by combining them.
 */
export default function ProtectedRoute({ children, role, roles, permission }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7]" data-testid="auth-loading">
                <div className="text-sm text-[#4B5565]">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/" replace />;

    // DEV-013A · funnel to the change-password screen when the account
    // is flagged for a forced password change. Never let a role/perm
    // redirect win over this — it must be checked first.
    if (user.force_password_change && !FORCED_CHANGE_ALLOWED.has(location.pathname)) {
        return <Navigate to="/settings/change-password" replace />;
    }

    const allowedRoles = roles || (role ? [role] : null);
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to={homeForRole(user.role)} replace />;
    }
    if (permission && !(user.permissions || []).includes(permission)) {
        return <Navigate to={homeForRole(user.role)} replace />;
    }
    return children;
}
